import DsIcon from "./ds/Icon";
import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { FiImage, FiChevronDown, FiArrowLeft, FiTrash2, FiUser, FiCompass, FiBarChart2, FiFileText, FiShare2, FiLoader, FiLock, FiMoreHorizontal, FiTag } from "react-icons/fi";
import { FaInstagram, FaTiktok, FaYoutube, FaFacebookF } from "react-icons/fa";
import { useAuth } from "../context/AuthContext";
import { useSocket } from "../context/SocketContext";
import { getMintSpaceById, updateMintSpace, getSupportThreads, uninstallApp, updateAppSettings } from "../api/mintSpace";
import { uploadMintSpaceLogo } from "../api/upload";
import { useTranslation } from "../context/TranslationContext";
import UserProfileLink from "./UserProfileLink";
import MyCampaignsPage from "../pages/MyCampaignsPage";
import CampaignDetailsPage from "../pages/CampaignDetailsPage";
import MintSpaceSupportPage from "../pages/MintSpaceSupportPage";
import MintSpaceSettingsPage from "../pages/MintSpaceSettingsPage";
import MintSpaceAnnouncementsPage from "../pages/MintSpaceAnnouncementsPage";
import MintSpaceAppStorePage from "../pages/MintSpaceAppStorePage";
import MintSpaceAppPricingPage from "../pages/MintSpaceAppPricingPage";
import MintSpaceRequestsPage from "../pages/MintSpaceRequestsPage";
import MintSpaceRefundRequestsPage from "../pages/MintSpaceRefundRequestsPage";
import MintSpacePromoCodesPage from "../pages/MintSpacePromoCodesPage";
import MintSpaceMembersPage from "../pages/MintSpaceMembersPage";
import MintSpaceAnalyticsPage from "../pages/MintSpaceAnalyticsPage";
import MintSpaceSubmissionsPage from "../pages/MintSpaceSubmissionsPage";
import MintSpaceAccountsPage from "../pages/MintSpaceAccountsPage";
import MintSpaceGatewayPage from "../pages/MintSpaceGatewayPage";
import MintSpaceCloudPage from "../pages/MintSpaceCloudPage";
import MintSpaceCoursesPage from "../pages/MintSpaceCoursesPage";
import MintSpaceLiveStreamPage from "../pages/MintSpaceLiveStreamPage";
import MintSpaceConsultingPage from "../pages/MintSpaceConsultingPage";
import MintSpaceAffiliatePage from "../pages/MintSpaceAffiliatePage";
import MintSpaceProductDetailsPage from "../pages/MintSpaceProductDetailsPage";
import {
  hasMintSpaceApp,
  canSeeMintSpaceApp,
  canSeeMintSpaceAppInstance,
  getMintSpaceAppDisplayName,
  getInstanceVisibility,
  normalizeInstalledApps,
  normalizeInstalledAppId,
  getInstanceDisplayName,
  getInstanceById,
  resolveInstanceFromNavId,
  instanceNavId,
  appNavIdToPageNav,
  mintSpaceAppIdToNav,
  GATEWAY_APP_ID,
  CLOUD_APP_ID,
  COURSES_APP_ID,
  LIVE_STREAM_APP_ID,
  CONSULTING_APP_ID,
  CREATOR_REWARDS_APP_ID,
  PRODUCT_EXCLUDED_APP_IDS,
  isProductFreeForJoin,
  resolveProductInstanceId,
  resolveAppCatalogEntry,
  purchasedProductIds,
  productUnlocksConsulting,
} from "../utils/mintSpaceApps";
import QuestionsModal from "./QuestionsModal";
import SpaceGate from "./SpaceGate";
import { AppScope } from "./ds/AppCover";
import MintSpaceAppPropertiesModal from "./MintSpaceAppPropertiesModal";
import SidebarTruncateTooltip from "./SidebarTruncateTooltip";
import { useImageLoader } from "../hooks/useImageLoader";
import MintSpaceRatingStars from "./MintSpaceRatingStars";
import {
  BusinessVerificationBadge,
  BusinessVerificationModal,
  useBusinessVerification,
} from "./kyc/BusinessVerification";
import { BUSINESS_VERIFICATION_ENABLED } from "../constants/kyc";
import { getMintSpaceAppStoreCatalog } from "../data/mintSpaceAppStoreCatalog";
import AppIcon from "./ds/AppIcon";
import Avatar from "./ds/Avatar";
import IconButton from "./ds/IconButton";
import Button from "./ds/Button";
import Menu, { MenuItem, MenuSeparator } from "./ds/Menu";
import Modal from "./ds/Modal";
import TextView from "./ds/TextView";
import { refusedUploadMessage } from "../utils/refusedUpload";
import { getShareUrl, shareOrCopyUrl } from "../utils/share";
import WindowNavPanels from "./WindowNavPanels";
import MintSpaceNavButton from "./motion/MintSpaceNavButton";
import { LayoutGroup } from "framer-motion";
import SpaceBar from "./SpaceBar";
import CreatorTabs from "./campaign/CreatorTabs";
import { SELECTED_TAB as SELECTED_CREATOR_TAB } from "./campaign/shared";
import MintSpaceCampaignsDiscoverPage from "../pages/MintSpaceCampaignsDiscoverPage";

/** localStorage key: set to "1" to log Mint Space tab / URL / back behavior in the console */
const MINT_SPACE_NAV_DEBUG_KEY = "DEBUG_MINT_SPACE_NAV";

const mintSpaceNavLog = (event, detail) => {
  if (typeof window !== "undefined" && window.localStorage?.getItem(MINT_SPACE_NAV_DEBUG_KEY) === "1") {
    console.debug(`[MintSpaceNav ${new Date().toISOString()}]`, event, detail);
  }
};

/**
 * The app a product sells, as a sidebar nav id. Its owner and moderators open that
 * instead of the product's sales page; null when the app is no longer installed.
 */
const productAppNavId = (mintSpace, product) => {
  const instanceId = resolveProductInstanceId(product, mintSpace);
  const inst = instanceId ? getInstanceById(mintSpace, instanceId) : null;
  return inst ? instanceNavId(inst) : null;
};

const buildAppNavItem = (appCatalog, navId, catalogAppId, mintSpace, instance = null) => {
  const app = appCatalog.find((a) => a.id === catalogAppId);
  if (!app) return null;
  const customName = instance
    ? getInstanceDisplayName(mintSpace, instance, app.name)
    : getMintSpaceAppDisplayName(mintSpace, catalogAppId, "");
  const visibility = getInstanceVisibility(mintSpace, instance);
  return {
    id: navId,
    label: customName || app.name,
    icon: app.icon,
    color: app.color,
    isApp: true,
    isCatalogApp: true,
    catalogAppId,
    instanceId: instance?.instanceId || null,
    visibility,
    canDelete: !!(instance && catalogAppId !== CREATOR_REWARDS_APP_ID),
  };
};

/** Figma's menu: an app has its App icon at size XS, a product the plain bag. */
const MintSpaceNavItemIcon = ({ item, isActive = false }) => {
  if (item.isProduct) return <DsIcon name="bag" size={20} className="shrink-0" />;
  if (item.catalogAppId) return <AppIcon app={item.catalogAppId} size={24} />;
  if (typeof item.icon === "string") return <DsIcon name={item.icon} size={20} className="shrink-0" />;
  const Icon = item.icon;
  return (
    <Icon
      className={`h-3.5 w-3.5 shrink-0 ${isActive ? "opacity-100" : "opacity-70"}`}
      strokeWidth={2}
    />
  );
};

/**
 * Tabs that stay mounted while you look at another one: they hold a live stream or a
 * call, which leaving the tab must not end. Every other tab loads its data when it
 * mounts and has no other way to refresh, so it starts fresh on each visit.
 */
const LIVE_SESSION_PANELS = new Set(["live-stream", "consulting"]);

/** Which app a panel belongs to, for its colour. Anything else, Home included, stays mint. */
const PANEL_APPS = {
  campaigns: "creator-rewards",
  analytics: "creator-rewards",
  submissions: "creator-rewards",
  discover: "creator-rewards",
  accounts: "creator-rewards",
  gateway: "gateway",
  cloud: "cloud",
  courses: "courses",
  consulting: "consulting",
  "live-stream": "live",
};

/** Pages that carry Creator Rewards' tabs along the top. */
const CREATOR_TAB_PAGES = new Set(["campaigns", "discover", "analytics", "submissions", "accounts"]);

/**
 * Single source of truth for which tab matches the current URL + loaded Mint Space.
 * Used on load, browser Back/Forward (?nav=), and when clamping invalid ?nav= for the user.
 */
function getEffectiveNavFromSearchParams(searchParams, ms, campaignInPath = null) {
  if (!ms) {
    const navOnly = searchParams.get("nav");
    const campaignOnly = searchParams.get("campaign") || campaignInPath;
    // Explicit ?nav= wins; ?campaign= alone means Creator Rewards while space is still loading
    if (navOnly) return navOnly;
    if (campaignOnly) return "campaigns";
    return "home";
  }
  const navParam = searchParams.get("nav");
  const campaignId = searchParams.get("campaign") || campaignInPath;
  const canAccess = !!(ms.isOwner || ms.isMember || ms.isModerator);
  if (!canAccess) {
    return "home";
  }
  // ?nav= must win over ?campaign= — otherwise ?nav=accounts&campaign=id keeps snapping to highlighted campaigns list
  if (navParam) {
    if (
      ["settings", "app-store", "pricing", "requests", "refunds", "promo-codes", "affiliate"].includes(navParam) &&
      !ms.isOwner
    ) {
      return hasMintSpaceApp(ms, CREATOR_REWARDS_APP_ID) ? "campaigns" : "home";
    }
    if (navParam === "support" && !ms.pages?.support?.enabled) {
      return ms.isOwner ? "settings" : "home";
    }
    if (
      navParam === "campaigns" &&
      !canSeeMintSpaceApp(ms, CREATOR_REWARDS_APP_ID, {
        isOwner: ms.isOwner,
        isModerator: ms.isModerator,
      })
    ) {
      return ms.isOwner ? "app-store" : "home";
    }
    if (
      navParam === "gateway" &&
      !canSeeMintSpaceApp(ms, GATEWAY_APP_ID, {
        isOwner: ms.isOwner,
        isModerator: ms.isModerator,
      })
    ) {
      return ms.isOwner ? "app-store" : "home";
    }
    if (
      navParam === "cloud" &&
      !canSeeMintSpaceApp(ms, CLOUD_APP_ID, {
        isOwner: ms.isOwner,
        isModerator: ms.isModerator,
      })
    ) {
      return ms.isOwner ? "app-store" : "home";
    }
    if (
      navParam === "courses" &&
      !canSeeMintSpaceApp(ms, COURSES_APP_ID, {
        isOwner: ms.isOwner,
        isModerator: ms.isModerator,
      })
    ) {
      return ms.isOwner ? "app-store" : "home";
    }
    if (
      navParam === "live-stream" &&
      !canSeeMintSpaceApp(ms, LIVE_STREAM_APP_ID, {
        isOwner: ms.isOwner,
        isModerator: ms.isModerator,
      })
    ) {
      return ms.isOwner ? "app-store" : "home";
    }
    if (
      navParam === "consulting" &&
      !canSeeMintSpaceApp(ms, CONSULTING_APP_ID, {
        isOwner: ms.isOwner,
        isModerator: ms.isModerator,
      })
    ) {
      return ms.isOwner ? "app-store" : "home";
    }
    // Priced products open their own pay page in the sidebar — for buyers. The people
    // who run the space get the app the product sells.
    if (navParam.startsWith("product-")) {
      const productId = navParam.slice("product-".length);
      const product = (ms.appPricing || []).find((p) => String(p._id) === productId);
      const appNav = product && (ms.isOwner || ms.isModerator) ? productAppNavId(ms, product) : null;
      if (appNav) return appNav;
      if (product && !isProductFreeForJoin(product)) return navParam;
      return "home";
    }
    if (navParam.startsWith("instance-")) {
      const inst = normalizeInstalledApps(ms.installedApps).find(
        (i) => instanceNavId(i) === navParam
      );
      // Per instance, not per app: two installs of the same app can differ, and a
      // buyer may have unlocked exactly one of them.
      if (
        inst &&
        canSeeMintSpaceAppInstance(ms, inst, {
          isOwner: ms.isOwner,
          isModerator: ms.isModerator,
        })
      ) {
        return navParam;
      }
      return ms.isOwner ? "app-store" : "home";
    }
    const legacyAppNavMap = {
      campaigns: CREATOR_REWARDS_APP_ID,
      gateway: GATEWAY_APP_ID,
      cloud: CLOUD_APP_ID,
      courses: COURSES_APP_ID,
      "live-stream": LIVE_STREAM_APP_ID,
      consulting: CONSULTING_APP_ID,
    };
    if (legacyAppNavMap[navParam]) {
      const inst = normalizeInstalledApps(ms.installedApps).find(
        (i) => i.appId === legacyAppNavMap[navParam]
      );
      if (
        inst &&
        canSeeMintSpaceApp(ms, inst.appId, {
          isOwner: ms.isOwner,
          isModerator: ms.isModerator,
        })
      ) {
        return instanceNavId(inst);
      }
      return ms.isOwner ? "app-store" : "home";
    }
    return navParam;
  }
  // No ?nav=: ?campaign= deep-link opens Creator Rewards
  if (campaignId && hasMintSpaceApp(ms, CREATOR_REWARDS_APP_ID)) {
    const inst = normalizeInstalledApps(ms.installedApps).find(
      (i) => i.appId === CREATOR_REWARDS_APP_ID
    );
    return inst ? instanceNavId(inst) : "campaigns";
  }
  return "home";
}

// Edit Space Form Component
const EditSpaceForm = ({ isDark, mintSpace, setToast, onSuccess }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    bio: mintSpace?.bio || "",
    logo: mintSpace?.logo || "",
  });
  const [logoPreview, setLogoPreview] = useState(mintSpace?.logo || "");
  const [logoFile, setLogoFile] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const logoInputRef = useRef(null);

  const handleLogoChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        setToast(t("windows.imageSizeMustBeLessThan10MB") || "Image size must be less than 10MB");
        return;
      }
      setLogoFile(file);
      // Show preview immediately
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result);
      };
      reader.readAsDataURL(file);
      
      // Upload to Backblaze
      try {
        const uploadResponse = await uploadMintSpaceLogo(file);
        if (uploadResponse.success) {
          setLogoPreview(uploadResponse.url);
        } else {
          throw new Error("Upload failed");
        }
      } catch (error) {
        console.error("Failed to upload logo:", error);
        setToast(refusedUploadMessage(error) || t("windows.failedToUploadLogo") || "Failed to upload logo image");
        setLogoFile(null);
        setLogoPreview("");
        if (logoInputRef.current) {
          logoInputRef.current.value = "";
        }
      }
    }
  };

  const handleRemoveLogo = () => {
    setLogoFile(null);
    setLogoPreview("");
    if (logoInputRef.current) {
      logoInputRef.current.value = "";
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    try {
      // If there's a new logo file, upload it first
      let logoUrl = logoPreview;
      if (logoFile && logoFile instanceof File) {
        try {
          const uploadResponse = await uploadMintSpaceLogo(logoFile);
          if (uploadResponse.success) {
            logoUrl = uploadResponse.url;
          }
        } catch (error) {
          setToast(refusedUploadMessage(error) || t("windows.failedToUploadLogoRetry") || "Failed to upload logo. Please try again.");
          setIsSaving(false);
          return;
        }
      }

      const result = await updateMintSpace(mintSpace._id, {
        bio: formData.bio,
        logo: logoUrl,
      });
      if (result.success) {
        setToast(t("windows.mintSpaceUpdatedSuccessfully") || "Mint Space updated successfully!");
        setLogoFile(null); // Clear file object after successful save
        onSuccess(result.data);
      }
    } catch (error) {
      setToast(error.message || t("windows.errorUpdatingMintSpace") || "Error updating Mint Space");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-space-20">
      <div className="flex flex-col gap-space-16">
        <div className="flex flex-col gap-space-8">
          <p className="text-footnote-semibold text-label-secondary">{t("windows.logoLabel")}</p>
          <div className="flex items-center gap-space-16">
            <Avatar size="l" src={logoPreview} name={mintSpace?.name} />
            <input ref={logoInputRef} type="file" accept="image/*" onChange={handleLogoChange} className="hidden" />
            <Button
              size="s"
              variant="tinted"
              icon={<DsIcon name="image" size={16} />}
              onClick={() => logoInputRef.current?.click()}
            >
              {t(logoPreview ? "windows.changeLogo" : "windows.addLogo")}
            </Button>
            {logoPreview && (
              <Button size="s" variant="plain" onClick={handleRemoveLogo}>
                {t("common.remove")}
              </Button>
            )}
          </div>
        </div>
        <TextView
          label={t("windows.bioLabel")}
          value={formData.bio}
          onChange={(e) => setFormData((prev) => ({ ...prev, bio: e.target.value }))}
          placeholder={t("windows.bioPlaceholder")}
        />
      </div>

      <div className="flex justify-end gap-space-12">
        <Button variant="gray" onClick={() => onSuccess(mintSpace)}>
          {t("common.cancel")}
        </Button>
        <Button type="submit" loading={isSaving}>
          {isSaving ? t("windows.saving") : t("windows.saveChanges")}
        </Button>
      </div>
    </form>
  );
};

const YourMintSpaceWindow = ({ isDark, setToast }) => {
  const { spaceId, id: campaignIdFromPath } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const { socket } = useSocket();
  const { t } = useTranslation();

  const getInitialNavFromUrl = () => {
    if (campaignIdFromPath) return "campaigns";
    const navParam = searchParams.get("nav");
    const campaignParam = searchParams.get("campaign");
    if (campaignParam) return "campaigns";
    if (navParam) return navParam;
    return "home";
  };

  const [activeNav, setActiveNav] = useState(getInitialNavFromUrl);
  const [pricingTarget, setPricingTarget] = useState(null); // { appId, instanceId } | null
  const [affiliateProductFocus, setAffiliateProductFocus] = useState(null);
  const [appPropertiesTarget, setAppPropertiesTarget] = useState(null); // { instanceId, catalogAppId }
  const [openAppMenu, setOpenAppMenu] = useState(null); // { item }
  const appMenuAnchor = useRef(null);
  const closeAppMenu = useCallback(() => setOpenAppMenu(null), []);

  useEffect(() => {
    if (campaignIdFromPath) {
      setActiveNav("campaigns");
    }
  }, [campaignIdFromPath]);

  /**
   * Tab change + URL update. Use replace:false when the user picks a tab so each tab is a
   * history entry and the browser Back button (and navigate(-1)) restore the previous tab.
   * Use replace:true for server-driven or corrective updates (no extra history entry).
   */
  const applyMintNav = useCallback(
    (nextNav, { replace = false, setCampaign } = {}) => {
      if (!spaceId) return;
      mintSpaceNavLog("applyMintNav", { nextNav, replace, setCampaign, stack: new Error().stack?.split("\n").slice(1, 5) });
      setActiveNav(nextNav);
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set("nav", nextNav);
          // ?product= is the dashboard's "Edit"; moving on from there means it's done.
          next.delete("product");
          // ?app= is the App Store page you were reading.
          next.delete("app");
          if (appNavIdToPageNav(nextNav, mintSpace) === "consulting") {
            // no-op; consultingTab may be set by onOpenApp
          } else {
            next.delete("consultingTab");
          }
          const keepCampaignHighlight = nextNav === "campaigns" || nextNav === "discover";
          if (setCampaign !== undefined) {
            if (setCampaign === null || setCampaign === "") {
              next.delete("campaign");
            } else {
              next.set("campaign", setCampaign);
            }
          } else if (!keepCampaignHighlight) {
            next.delete("campaign");
          } else {
            const campaign = prev.get("campaign");
            if (campaign) next.set("campaign", campaign);
          }
          return next;
        },
        { replace }
      );
    },
    [spaceId, setSearchParams]
  );

  /** Prefer real history so Back returns to the previous route (dashboard, discover, etc.). */
  const handleMintSpaceBack = useCallback(() => {
    mintSpaceNavLog("handleMintSpaceBack", { historyLength: typeof window !== "undefined" ? window.history.length : null });
    if (typeof window !== "undefined" && window.history.length > 1) {
      navigate(-1);
    } else {
      navigate("/home");
    }
  }, [navigate]);
  const [mintSpace, setMintSpace] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showQuestionsModal, setShowQuestionsModal] = useState(false);
  const [headerImagePreview, setHeaderImagePreview] = useState(null);
  const [isBannerUploading, setIsBannerUploading] = useState(false);
  const [editDropdownOpen, setEditDropdownOpen] = useState(false);
  const [isOwner, setIsOwner] = useState(false);
  const [isMember, setIsMember] = useState(false);
  const [isModerator, setIsModerator] = useState(false);
  const [supportUnreadCount, setSupportUnreadCount] = useState(0);
  // Buyers' refund requests waiting on the owner: from the space, then kept current by the
  // Refunds tab as the owner decides.
  const [pendingRefunds, setPendingRefunds] = useState(0);
  useEffect(() => {
    setPendingRefunds(mintSpace?.pendingRefundRequests || 0);
  }, [mintSpace?.pendingRefundRequests]);
  const verification = useBusinessVerification(
    mintSpace?._id,
    isOwner && BUSINESS_VERIFICATION_ENABLED
  );
  const campaignParamProcessedRef = useRef(false);
  const bannerUploadingRef = useRef(false);
  const fileInputRef = useRef(null);
  const dropdownRef = useRef(null);
  const menuImageRef = useRef(null);
  const closeEditDropdown = useCallback(() => setEditDropdownOpen(false), []);
  const containerRef = useRef(null);
  const { imagesLoaded } = useImageLoader(containerRef);

  const appCatalog = useMemo(() => getMintSpaceAppStoreCatalog(t), [t]);


  // The load below runs once per space. What it calls changes identity on every URL
  // change — react-router rebuilds setSearchParams, and applyMintNav with it — so it
  // reads them from here: as dependencies, they would reload the whole space and
  // remount every panel on every tab click.
  const loadContextRef = useRef(null);
  loadContextRef.current = { applyMintNav, searchParams, setToast, t };

  useEffect(() => {
    let cancelled = false;
    const fetchMintSpace = async () => {
      if (!spaceId) {
        setLoading(false);
        return;
      }

      // Reset state when spaceId changes
      setMintSpace(null);
      setIsOwner(false);
      setIsMember(false);
      setIsModerator(false);
      setHeaderImagePreview(null);
      setLoading(true);
      // Reset the processed flag when spaceId changes
      campaignParamProcessedRef.current = false;

      try {
        const result = await getMintSpaceById(spaceId);
        // Another space was opened while this one loaded.
        if (cancelled) return;
        const { applyMintNav, searchParams } = loadContextRef.current;
        if (result.success && result.data) {
          setMintSpace(result.data);
          setIsOwner(result.data.isOwner || false);
          setIsMember(result.data.isMember || false);
          setIsModerator(result.data.isModerator || false);
          if (result.data.logo) {
            setHeaderImagePreview(result.data.logo);
          }
          // Set initial active nav based on installed apps and membership
          // Check if campaign query param exists (only process once per spaceId)
          const campaignId = searchParams.get("campaign");
          if (
            campaignId &&
            !campaignParamProcessedRef.current &&
            result.data.installedApps &&
            hasMintSpaceApp(result.data, CREATOR_REWARDS_APP_ID)
          ) {
            campaignParamProcessedRef.current = true;
            const navOk = searchParams.get("nav") === "campaigns";
            const campaignOk = searchParams.get("campaign") === campaignId;
            if (!navOk || !campaignOk) {
              applyMintNav("campaigns", { replace: true, setCampaign: campaignId });
            } else {
              mintSpaceNavLog("fetchSkipApplyMintNav", { reason: "urlAlreadyMatchesCampaignHighlight" });
            }
          }
        }
      } catch (error) {
        if (cancelled) return;
        const { setToast, t } = loadContextRef.current;
        setToast(t("mintSpace.errorLoadingMintSpace") || "Error loading Mint Space");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchMintSpace();
    return () => {
      cancelled = true;
    };
  }, [spaceId]);

  // Align active tab with ?nav= / ?campaign= when the URL changes (Back/Forward) or after Mint Space loads.
  // A fresh copy of the space (a save, the quiet refresh) runs this too, and the URL can
  // still lag a tab click then — following it would bounce back to the tab just left —
  // so the active tab follows the URL only when the URL itself has moved.
  const syncedUrlRef = useRef(null);
  useEffect(() => {
    if (!mintSpace) return;
    const effective = getEffectiveNavFromSearchParams(searchParams, mintSpace, campaignIdFromPath);
    const navInUrl = searchParams.get("nav");
    if (effective !== navInUrl) {
      mintSpaceNavLog("syncUrlClamp", { effective, navInUrl, query: searchParams.toString() });
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set("nav", effective);
          const c = prev.get("campaign");
          if (c) next.set("campaign", c);
          return next;
        },
        { replace: true }
      );
    }
    const urlKey = `${mintSpace._id}?${searchParams.toString()}`;
    if (syncedUrlRef.current === urlKey) return;
    syncedUrlRef.current = urlKey;
    setActiveNav((prev) => {
      if (prev === effective) return prev;
      mintSpaceNavLog("syncActiveNavFromUrl", { from: prev, to: effective, query: searchParams.toString() });
      return effective;
    });
  }, [searchParams, mintSpace, setSearchParams, campaignIdFromPath]);

  useEffect(() => {
    if (!mintSpace?._id || !mintSpace.pages?.support?.enabled) return;
    if (!isOwner && !isModerator) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await getSupportThreads(mintSpace._id);
        if (
          !cancelled &&
          res.success &&
          res.data &&
          typeof res.data.totalUnread === "number"
        ) {
          setSupportUnreadCount(res.data.totalUnread);
        }
      } catch {
        /* ignore */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [mintSpace?._id, mintSpace?.pages?.support?.enabled, isOwner, isModerator]);

  useEffect(() => {
    if (!socket || !mintSpace?._id || !mintSpace.pages?.support?.enabled) return;
    if (!isOwner && !isModerator) return;
    socket.emit("join_mintspace_staff", { mintSpaceId: mintSpace._id });
    const onBump = () => {
      getSupportThreads(mintSpace._id)
        .then((res) => {
          if (res.success && res.data && typeof res.data.totalUnread === "number") {
            setSupportUnreadCount(res.data.totalUnread);
          }
        })
        .catch(() => {});
    };
    socket.on("support_new_message", onBump);
    return () => {
      socket.off("support_new_message", onBump);
    };
  }, [socket, mintSpace?._id, mintSpace?.pages?.support?.enabled, isOwner, isModerator]);

  const handleImageChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (bannerUploadingRef.current) return;
    if (file.size > 5 * 1024 * 1024) {
      setToast(t("windows.imageSizeMustBeLessThan5MB") || "Image size must be less than 5MB");
      e.target.value = "";
      return;
    }
    const prevPreview = headerImagePreview;
    const objectUrl = URL.createObjectURL(file);
    setHeaderImagePreview(objectUrl);
    setEditDropdownOpen(false);
    bannerUploadingRef.current = true;
    setIsBannerUploading(true);
    try {
      const uploadResponse = await uploadMintSpaceLogo(file);
      if (!uploadResponse.success) {
        throw new Error("Upload failed");
      }
      const result = await updateMintSpace(spaceId, { logo: uploadResponse.url });
      if (!result.success) {
        throw new Error("Update failed");
      }
      URL.revokeObjectURL(objectUrl);
      handleMintSpaceUpdate(result.data);
      setToast(t("mintSpace.imageUpdatedSuccessfully") || "Image updated successfully");
    } catch (error) {
      console.error(error);
      URL.revokeObjectURL(objectUrl);
      setHeaderImagePreview(prevPreview);
      setToast(refusedUploadMessage(error) || t("mintSpace.errorUpdatingImage") || "Error updating image");
    } finally {
      bannerUploadingRef.current = false;
      setIsBannerUploading(false);
      e.target.value = "";
    }
  };

  const handleRemoveImage = async () => {
    if (bannerUploadingRef.current) return;
    try {
      const result = await updateMintSpace(spaceId, { logo: "" });
      if (result.success) {
        handleMintSpaceUpdate(result.data);
        setHeaderImagePreview(null);
        setToast(t("mintSpace.imageRemovedSuccessfully") || "Image removed successfully");
      }
    } catch (error) {
      setToast(t("mintSpace.errorRemovingImage") || "Error removing image");
    }
    if (menuImageRef.current) menuImageRef.current.value = "";
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  /** Counts updates applied to the space, so an older background read can tell it's stale. */
  const spaceVersionRef = useRef(0);

  const handleMintSpaceUpdate = (updatedSpace) => {
    spaceVersionRef.current += 1;
    setMintSpace((prev) => {
      const merged = { ...updatedSpace };
      if (!Array.isArray(merged.installedApps) && prev?.installedApps?.length) {
        merged.installedApps = prev.installedApps;
      }
      if (merged.logo === undefined && prev?.logo) {
        merged.logo = prev.logo;
      }
      return merged;
    });
    // Install/uninstall APIs once returned raw documents without isOwner — preserve prior flags when omitted
    setIsOwner((prev) => (typeof updatedSpace.isOwner === "boolean" ? updatedSpace.isOwner : prev));
    setIsMember((prev) => (typeof updatedSpace.isMember === "boolean" ? updatedSpace.isMember : prev));
    setIsModerator((prev) => (typeof updatedSpace.isModerator === "boolean" ? updatedSpace.isModerator : prev));
    if (updatedSpace.logo) {
      setHeaderImagePreview(updatedSpace.logo);
    } else if (updatedSpace.logo === "" || updatedSpace.logo === null) {
      setHeaderImagePreview(null);
    }

    const effectiveApps = Array.isArray(updatedSpace.installedApps)
      ? updatedSpace.installedApps
      : mintSpace?.installedApps || [];

    // If currently viewing Creator Rewards and it's uninstalled, switch to App Store (only if owner)
    if (activeNav === "campaigns" && !hasMintSpaceApp({ installedApps: effectiveApps }, CREATOR_REWARDS_APP_ID)) {
      const ownerNow =
        typeof updatedSpace.isOwner === "boolean" ? updatedSpace.isOwner : isOwner;
      if (ownerNow) {
        applyMintNav("app-store", { replace: true });
      } else {
        applyMintNav("home", { replace: true });
      }
    }
    if (activeNav === "gateway" && !hasMintSpaceApp({ installedApps: effectiveApps }, GATEWAY_APP_ID)) {
      const ownerNow =
        typeof updatedSpace.isOwner === "boolean" ? updatedSpace.isOwner : isOwner;
      if (ownerNow) {
        applyMintNav("app-store", { replace: true });
      } else {
        applyMintNav("home", { replace: true });
      }
    }
    if (activeNav === "cloud" && !hasMintSpaceApp({ installedApps: effectiveApps }, CLOUD_APP_ID)) {
      const ownerNow =
        typeof updatedSpace.isOwner === "boolean" ? updatedSpace.isOwner : isOwner;
      if (ownerNow) {
        applyMintNav("app-store", { replace: true });
      } else {
        applyMintNav("home", { replace: true });
      }
    }
    if (activeNav === "courses" && !hasMintSpaceApp({ installedApps: effectiveApps }, COURSES_APP_ID)) {
      const ownerNow =
        typeof updatedSpace.isOwner === "boolean" ? updatedSpace.isOwner : isOwner;
      if (ownerNow) {
        applyMintNav("app-store", { replace: true });
      } else {
        applyMintNav("home", { replace: true });
      }
    }
    if (activeNav === "live-stream" && !hasMintSpaceApp({ installedApps: effectiveApps }, LIVE_STREAM_APP_ID)) {
      const ownerNow =
        typeof updatedSpace.isOwner === "boolean" ? updatedSpace.isOwner : isOwner;
      if (ownerNow) {
        applyMintNav("app-store", { replace: true });
      } else {
        applyMintNav("home", { replace: true });
      }
    }
    if (activeNav === "consulting" && !hasMintSpaceApp({ installedApps: effectiveApps }, CONSULTING_APP_ID)) {
      const ownerNow =
        typeof updatedSpace.isOwner === "boolean" ? updatedSpace.isOwner : isOwner;
      if (ownerNow) {
        applyMintNav("app-store", { replace: true });
      } else {
        applyMintNav("home", { replace: true });
      }
    }
    if (activeNav === "support" && !updatedSpace.pages?.support?.enabled) {
      applyMintNav("settings", { replace: true });
    }
  };

  // Switching tabs doesn't reload the space, so its own figures — the member count, the
  // refunds badge — are refreshed quietly instead. A save applied in the meantime is
  // newer than this read, which then gives way.
  const handleMintSpaceUpdateRef = useRef(handleMintSpaceUpdate);
  handleMintSpaceUpdateRef.current = handleMintSpaceUpdate;
  const loadedSpaceIdRef = useRef(null);
  loadedSpaceIdRef.current = mintSpace?._id ? String(mintSpace._id) : null;
  const refreshedForNavRef = useRef(activeNav);
  useEffect(() => {
    if (refreshedForNavRef.current === activeNav) return undefined;
    refreshedForNavRef.current = activeNav;
    if (loadedSpaceIdRef.current !== String(spaceId) || bannerUploadingRef.current) return undefined;
    const version = spaceVersionRef.current;
    let cancelled = false;
    getMintSpaceById(spaceId)
      .then((res) => {
        if (cancelled || version !== spaceVersionRef.current || !res?.success || !res.data) return;
        handleMintSpaceUpdateRef.current(res.data);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [activeNav, spaceId]);

  useEffect(() => {
    if (!mintSpace || activeNav !== "support") return;
    if (mintSpace.pages?.support?.enabled) return;
    if (isOwner) applyMintNav("settings", { replace: true });
    else applyMintNav("home", { replace: true });
  }, [mintSpace, activeNav, isOwner, applyMintNav]);

  const baseText = "text-label-primary";
  const mutedText = "text-label-secondary";
  const cardBase = "border-separator-default bg-bg-raised";
  const sidebarBase = "bg-bg-raised border-separator-default";
  const iconBtn = isDark
    ? "bg-black/50 hover:bg-black/70 text-white backdrop-blur-sm"
    : "bg-white/80 hover:bg-white text-slate-900 backdrop-blur-sm";

  // Build navigation items dynamically based on installed apps
  const appVisibilityOpts = { isOwner, isModerator };
  const boughtProductIds = purchasedProductIds(mintSpace);

  const regularNavItems = [
    { id: "home", label: t("navigation.home") || "Home", icon: "home" },
    // Newest first: an app just added shows at the top, under Home. Installs are stored
    // oldest first.
    ...normalizeInstalledApps(mintSpace?.installedApps, mintSpace)
      .reverse()
      .filter((inst) =>
        canSeeMintSpaceAppInstance(mintSpace, inst, appVisibilityOpts)
      )
      .map((inst) => {
        const pageNav = mintSpaceAppIdToNav(inst.appId);
        if (!pageNav) return null;
        return buildAppNavItem(
          appCatalog,
          instanceNavId(inst),
          inst.appId,
          mintSpace,
          inst
        );
      })
      .filter(Boolean),
    ...(mintSpace?.appPricing || [])
      .filter((p) => !PRODUCT_EXCLUDED_APP_IDS.includes(normalizeInstalledAppId(p.appId)))
      .filter((p) => !isProductFreeForJoin(p))
      // A product here is a sales page, for members who haven't bought it. Once bought,
      // its apps are in the list above. Owners and moderators already see every app,
      // and manage products in the Dashboard, so for them it would be a duplicate.
      .filter((p) => !isOwner && !isModerator && !boughtProductIds.has(String(p._id)))
      .map((p) => {
        const instanceId = resolveProductInstanceId(p, mintSpace);
        const inst = instanceId ? getInstanceById(mintSpace, instanceId) : null;
        const catalogApp = resolveAppCatalogEntry(appCatalog, p.appId);
        const label =
          (p.name || "").trim() ||
          getInstanceDisplayName(mintSpace, inst, catalogApp?.name || "") ||
          t("mintSpace.product") ||
          "Product";
        return {
          id: `product-${p._id}`,
          label,
          icon: FiTag,
          isProduct: true,
          color: catalogApp?.color,
        };
      }),
    { id: "members", label: t("mintSpace.members") || "Members", icon: "users" },
    ...(mintSpace?.pages?.support?.enabled && isMember && !isOwner && !isModerator
      ? [{ id: "support", label: t("navigation.supportChat") || "Support Chat", icon: "message" }]
      : []),
  ];

  // Admin Settings — owners get full admin list; moderators get Support when enabled
  const adminNavItems = isOwner
    ? [
        { id: "settings", label: t("mintSpaceSettings.brandDashboard") || "Dashboard", icon: "settings" },
        { id: "requests", label: t("mintSpace.requests") || "Requests", icon: "user-plus" },
        { id: "refunds", label: t("refunds.navLabel"), icon: "wallet", badge: pendingRefunds },
        ...(mintSpace?.pages?.support?.enabled
          ? [
              {
                id: "support",
                label: t("navigation.supportChat") || "Support Chat",
                icon: "message",
                badge: supportUnreadCount,
              },
            ]
          : []),
        { id: "app-store", label: t("navigation.appStore") || "App Store", icon: "bag" },
        { id: "pricing", label: t("mintSpace.createProduct") || "Create Product", icon: "plus" },
        { id: "promo-codes", label: t("promoCodes.navLabel"), icon: "filter" },
        { id: "affiliate", label: t("navigation.affiliates") || "Affiliates", icon: "megaphone" },
      ]
    : isModerator && mintSpace?.pages?.support?.enabled
      ? [
          {
            id: "support",
            label: t("navigation.supportChat") || "Support Chat",
            icon: "message",
            badge: supportUnreadCount,
          },
        ]
      : [];

  const spaceBarItems = [...adminNavItems, ...regularNavItems];

  if (loading) {
    return (
      <div className="w-full h-full flex items-center justify-center">
        <div className={`text-lg ${mutedText}`}>{t("windows.loading") || "Loading..."}</div>
      </div>
    );
  }

  if (!mintSpace) {
    return (
      <div className="w-full h-full flex items-center justify-center">
        <div className={`text-lg ${mutedText}`}>{t("mintSpace.mintSpaceNotFound") || "Mint Space not found"}</div>
      </div>
    );
  }

  // Get connected accounts count
  const getConnectedAccountsCount = () => {
    if (!user) return 0;
    // Handle both user.socials and user.profile.socials structures
    const socials = user.socials || user.profile?.socials || {};
    let count = 0;
    if (socials.instagram && socials.instagram.trim() !== "") count++;
    if (socials.youtube && socials.youtube.trim() !== "") count++;
    if (socials.tiktok && socials.tiktok.trim() !== "") count++;
    if (socials.facebook && socials.facebook.trim() !== "") count++;
    return count;
  };

  // Build navbar items
  const navbarItems = [
    { id: "analytics", label: t("navigation.analytics") || "Analytics", icon: FiBarChart2 },
    { id: "submissions", label: t("navigation.submissions") || "Submissions", icon: FiFileText },
    { id: "discover", label: t("navigation.discover") || "Discover", icon: FiCompass },
    { id: "accounts", label: `${t("navigation.accounts") || "Accounts"} (${getConnectedAccountsCount()})`, icon: FiUser },
  ];

  /**
   * One page of the window. `panelKey` is the nav entry the panel stands for: Live
   * Stream and Consulting stay mounted while hidden, so they must keep their own app
   * copy rather than follow whichever one is open now.
   */
  const onHome = appNavIdToPageNav(activeNav, mintSpace) === "home";

  // Pages that scroll keep 96px free at the bottom on desktop (lg:pb-[96px]), so their
  // last button can scroll clear of the floating Messages bar.
  const renderMintNavPanel = (navId, panelKey = activeNav) => {
    if (navId.startsWith("product-")) {
      const productId = navId.slice("product-".length);
      return (
        <div className="p-3 lg:px-space-24 lg:pb-[96px] lg:pt-space-16">
          <MintSpaceProductDetailsPage
            embedded
            isDark={isDark}
            setToast={setToast}
            spaceId={spaceId}
            productId={productId}
            mintSpace={mintSpace}
            membership={{
              isOwner,
              isMember,
              isModerator,
              hasPendingRequest: !!mintSpace?.hasPendingRequest,
            }}
            onJoinSuccess={async () => {
              const refreshResult = await getMintSpaceById(spaceId);
              if (refreshResult.success && refreshResult.data) {
                handleMintSpaceUpdate(refreshResult.data);
              }
            }}
            onOpenApp={async (appId, instanceId, productId) => {
              let ms = mintSpace;
              try {
                const refreshResult = await getMintSpaceById(spaceId);
                if (refreshResult.success && refreshResult.data) {
                  handleMintSpaceUpdate(refreshResult.data);
                  ms = refreshResult.data;
                }
              } catch {
                /* use cached space */
              }
              const apps = normalizeInstalledApps(ms?.installedApps, ms);
              const wantedApp = normalizeInstalledAppId(appId);
              let inst =
                (instanceId && apps.find((i) => String(i.instanceId) === String(instanceId))) ||
                null;
              if (!inst && productId && ms?.appPricing?.length) {
                const prod = ms.appPricing.find((p) => String(p._id) === String(productId));
                const resolved = prod ? resolveProductInstanceId(prod, ms) : null;
                if (resolved) {
                  inst = apps.find((i) => String(i.instanceId) === String(resolved)) || null;
                }
              }
              if (!inst) {
                inst = apps.find((i) => normalizeInstalledAppId(i.appId) === wantedApp) || null;
              }
              if (!inst) {
                setToast?.(
                  t("mintSpace.openAppFailed") ||
                    "Could not open the app. Refresh the page or open Consulting from the sidebar."
                );
                return;
              }
              const nav = instanceNavId(inst);
              const product =
                productId && ms?.appPricing
                  ? ms.appPricing.find((p) => String(p._id) === String(productId))
                  : null;
              setActiveNav(nav);
              setSearchParams(
                (prev) => {
                  const next = new URLSearchParams(prev);
                  next.set("nav", nav);
                  next.delete("product");
                  if (productUnlocksConsulting(product || { appId: inst.appId })) {
                    next.set("consultingTab", "sessions");
                  } else {
                    next.delete("consultingTab");
                  }
                  return next;
                },
                { replace: false }
              );
            }}
          />
        </div>
      );
    }

    const panelInstance = resolveInstanceFromNavId(panelKey, mintSpace);
    const appInstanceId = panelInstance?.instanceId || null;

    switch (navId) {
      case "campaigns":
        if (campaignIdFromPath) {
          return (
            <div className="p-3 lg:px-space-24 lg:pb-[96px] lg:pt-space-16">
              <CampaignDetailsPage
                isDark={isDark}
                setToast={setToast}
                campaignId={campaignIdFromPath}
                onNavigate={(nav) => applyMintNav(nav)}
              />
            </div>
          );
        }
        return (
          <div className="p-3 lg:px-space-24 lg:pb-[96px] lg:pt-space-16">
            <MyCampaignsPage
              isDark={isDark}
              setToast={setToast}
              spaceId={spaceId}
              isOwner={isOwner}
              canCharge={isOwner || isModerator}
            />
          </div>
        );
      case "home":
        return (
          <div className="p-3 lg:px-space-24 lg:pb-[96px] lg:pt-space-16">
            <MintSpaceAnnouncementsPage
              isDark={isDark}
              setToast={setToast}
              mintSpace={mintSpace}
              onUpdate={handleMintSpaceUpdate}
              isOwner={isOwner || isModerator}
              type="post"
              onNavigate={(nav) => applyMintNav(nav)}
              onEditSpace={() => setShowEditModal(true)}
            />
          </div>
        );
      case "gateway":
        if (!hasMintSpaceApp(mintSpace, GATEWAY_APP_ID)) return null;
        return (
          <div className="flex min-h-0 flex-1 flex-col p-3 lg:px-space-24 lg:py-space-16">
            <MintSpaceGatewayPage
              isDark={isDark}
              setToast={setToast}
              mintSpace={mintSpace}
              isAdmin={isOwner || isModerator}
              appInstanceId={appInstanceId}
              onCreateProduct={() => {
                setPricingTarget({ appId: GATEWAY_APP_ID, instanceId: appInstanceId });
                applyMintNav("pricing", { replace: false });
              }}
              onOpenProperties={
                isOwner && appInstanceId
                  ? () =>
                      openAppProperties({
                        catalogAppId: GATEWAY_APP_ID,
                        instanceId: appInstanceId,
                      })
                  : undefined
              }
            />
          </div>
        );
      case "cloud":
        if (!hasMintSpaceApp(mintSpace, CLOUD_APP_ID)) return null;
        return (
          <div className="flex min-h-0 flex-1 flex-col p-3 lg:px-space-24 lg:py-space-16">
            <MintSpaceCloudPage
              isDark={isDark}
              setToast={setToast}
              mintSpace={mintSpace}
              isOwner={isOwner}
              isAdmin={isOwner || isModerator}
              appInstanceId={appInstanceId}
              onCreateProduct={() => {
                setPricingTarget({ appId: CLOUD_APP_ID, instanceId: appInstanceId });
                applyMintNav("pricing", { replace: false });
              }}
              onOpenProperties={
                isOwner && appInstanceId
                  ? () =>
                      openAppProperties({
                        catalogAppId: CLOUD_APP_ID,
                        instanceId: appInstanceId,
                      })
                  : undefined
              }
            />
          </div>
        );
      case "courses":
        if (!hasMintSpaceApp(mintSpace, COURSES_APP_ID)) return null;
        return (
          <div className="flex min-h-0 flex-1 flex-col p-3 lg:px-space-24 lg:py-space-16">
            <MintSpaceCoursesPage
              isDark={isDark}
              setToast={setToast}
              mintSpace={mintSpace}
              isAdmin={isOwner}
              appInstanceId={appInstanceId}
              onCreateProduct={() => {
                setPricingTarget({ appId: COURSES_APP_ID, instanceId: appInstanceId });
                applyMintNav("pricing", { replace: false });
              }}
              onOpenProperties={
                isOwner && appInstanceId
                  ? () =>
                      openAppProperties({
                        catalogAppId: COURSES_APP_ID,
                        instanceId: appInstanceId,
                      })
                  : undefined
              }
            />
          </div>
        );
      case "consulting":
        if (!hasMintSpaceApp(mintSpace, CONSULTING_APP_ID)) return null;
        return (
          <div className="flex min-h-0 flex-1 flex-col p-3 lg:px-space-24 lg:py-space-16">
            <MintSpaceConsultingPage
              isDark={isDark}
              setToast={setToast}
              mintSpace={mintSpace}
              isAdmin={isOwner || isModerator}
              appInstanceId={appInstanceId}
              initialEngagementId={searchParams.get("engagement") || undefined}
              initialHubTab={
                searchParams.get("consultingTab") === "sessions" ? "sessions" : undefined
              }
              initialOpenOffering={searchParams.get("offering") || undefined}
              onMintSpaceSaved={handleMintSpaceUpdate}
              onClearOfferingUrl={() => {
                setSearchParams(
                  (prev) => {
                    const next = new URLSearchParams(prev);
                    next.delete("offering");
                    return next;
                  },
                  { replace: true }
                );
              }}
              onOpenAffiliate={isOwner ? openAffiliateForProduct : undefined}
              onOpenProperties={
                isOwner && appInstanceId
                  ? () =>
                      openAppProperties({
                        catalogAppId: CONSULTING_APP_ID,
                        instanceId: appInstanceId,
                      })
                  : undefined
              }
            />
          </div>
        );
      case "live-stream":
        if (!hasMintSpaceApp(mintSpace, LIVE_STREAM_APP_ID)) return null;
        return (
          <div className="flex min-h-0 flex-1 flex-col p-3 lg:px-space-24 lg:py-space-16">
            <MintSpaceLiveStreamPage
              isDark={isDark}
              setToast={setToast}
              mintSpace={mintSpace}
              isAdmin={isOwner || isModerator}
              appInstanceId={appInstanceId}
              onCreateProduct={() => {
                setPricingTarget({ appId: LIVE_STREAM_APP_ID, instanceId: appInstanceId });
                applyMintNav("pricing", { replace: false });
              }}
              onOpenProperties={
                isOwner && appInstanceId
                  ? () =>
                      openAppProperties({
                        catalogAppId: LIVE_STREAM_APP_ID,
                        instanceId: appInstanceId,
                      })
                  : undefined
              }
            />
          </div>
        );
      case "affiliate":
        if (!isOwner) return null;
        return (
          <div className="p-3 lg:px-space-24 lg:pb-[96px] lg:pt-space-16">
            <MintSpaceAffiliatePage
              isDark={isDark}
              setToast={setToast}
              mintSpace={mintSpace}
              initialProductId={affiliateProductFocus || searchParams.get("affiliateProduct") || undefined}
            />
          </div>
        );
      case "settings":
        if (!isOwner) return null;
        return (
          <div className="px-space-8 pb-3 pt-1 lg:p-0 lg:pr-space-24">
            <MintSpaceSettingsPage
              isDark={isDark}
              setToast={setToast}
              mintSpace={mintSpace}
              onUpdate={handleMintSpaceUpdate}
              fullHeight
            />
          </div>
        );
      case "support":
        if (!mintSpace.pages?.support?.enabled || !(isOwner || isModerator || isMember)) return null;
        return (
          <div className="p-3 lg:px-space-24 lg:pb-[96px] lg:pt-space-16">
            <MintSpaceSupportPage
              isDark={isDark}
              setToast={setToast}
              mintSpace={mintSpace}
              onSupportUnreadChange={setSupportUnreadCount}
              isMintSpaceOwner={isOwner}
              isMintSpaceModerator={isModerator}
            />
          </div>
        );
      case "requests":
        return (
          <div className="p-3 lg:px-space-24 lg:pb-[96px] lg:pt-space-16">
            <MintSpaceRequestsPage
              isDark={isDark}
              setToast={setToast}
              mintSpaceId={spaceId}
            />
          </div>
        );
      case "refunds":
        if (!isOwner) return null;
        return (
          <div className="p-3 lg:px-space-24 lg:pb-[96px] lg:pt-space-16">
            <MintSpaceRefundRequestsPage
              isDark={isDark}
              setToast={setToast}
              mintSpaceId={spaceId}
              onPendingCountChange={setPendingRefunds}
            />
          </div>
        );
      case "promo-codes":
        if (!isOwner) return null;
        return (
          <div className="p-3 lg:px-space-24 lg:pb-[96px] lg:pt-space-16">
            <MintSpacePromoCodesPage isDark={isDark} setToast={setToast} mintSpaceId={spaceId} spaceName={mintSpace?.name} />
          </div>
        );
      case "members":
        return (
          <div className="p-3 lg:px-space-24 lg:pb-[96px] lg:pt-space-16">
            <MintSpaceMembersPage
              isDark={isDark}
              setToast={setToast}
              isOwner={isOwner}
            />
          </div>
        );
      case "analytics":
        return (
          <div className="p-3 lg:px-space-24 lg:pb-[96px] lg:pt-space-16">
            <MintSpaceAnalyticsPage
              isDark={isDark}
              setToast={setToast}
              mintSpaceId={spaceId}
              onFindCampaign={() => applyMintNav("discover", { replace: false })}
            />
          </div>
        );
      case "submissions":
        return (
          <div className="p-3 lg:px-space-24 lg:pb-[96px] lg:pt-space-16">
            <MintSpaceSubmissionsPage
              isDark={isDark}
              setToast={setToast}
              mintSpaceId={spaceId}
            />
          </div>
        );
      case "accounts":
        return (
          <div className="p-3 lg:px-space-24 lg:pb-[96px] lg:pt-space-16">
            <MintSpaceAccountsPage
              isDark={isDark}
              setToast={setToast}
            />
          </div>
        );
      case "discover":
        return (
          <div className="p-3 lg:px-space-24 lg:pb-[96px] lg:pt-space-16">
            <MintSpaceCampaignsDiscoverPage setToast={setToast} spaceId={spaceId} spaceName={mintSpace?.name} />
          </div>
        );
      case "app-store":
        if (!isOwner) return null;
        return (
          <MintSpaceAppStorePage
            mintSpaceId={mintSpace?._id}
            isDark={isDark}
            setToast={setToast}
            mintSpace={mintSpace}
            onAppChange={handleMintSpaceUpdate}
            onOpenApp={(navId) => applyMintNav(navId, { replace: false })}
            onOpenProperties={openAppProperties}
          />
        );
      case "pricing": {
        if (!isOwner) return null;
        // The dashboard's "Edit" opens one saved product; otherwise a new one is made.
        const editProductId = searchParams.get("product");
        return (
          <div className="p-3 lg:px-space-24 lg:pb-[96px] lg:pt-space-16">
            <MintSpaceAppPricingPage
              key={
                editProductId
                  ? `product-${editProductId}`
                  : pricingTarget
                  ? `${pricingTarget.appId}-${pricingTarget.instanceId || "app"}`
                  : "choose-apps"
              }
              isDark={isDark}
              setToast={setToast}
              mintSpace={mintSpace}
              initialProductId={editProductId || undefined}
              initialAppId={editProductId ? undefined : pricingTarget?.appId}
              initialInstanceId={editProductId ? undefined : pricingTarget?.instanceId}
              onSaved={handleMintSpaceUpdate}
            />
          </div>
        );
      }
      default:
        return null;
    }
  };

  const openAppProperties = (item) => {
    if (!isOwner || !item?.catalogAppId || item.catalogAppId === CREATOR_REWARDS_APP_ID) return;
    setAppPropertiesTarget({
      instanceId: item.instanceId,
      catalogAppId: item.catalogAppId,
    });
  };

  const handleToggleAppVisibility = async (item) => {
    if (!isOwner || !item?.instanceId || !item?.catalogAppId) return;
    if (item.catalogAppId === CREATOR_REWARDS_APP_ID) return;
    const nextVisibility = item.visibility === "private" ? "public" : "private";
    setOpenAppMenu(null);
    try {
      const result = await updateAppSettings(spaceId, {
        instanceId: item.instanceId,
        appId: item.catalogAppId,
        visibility: nextVisibility,
      });
      if (result.success) {
        handleMintSpaceUpdate(result.data);
        setToast(
          nextVisibility === "public"
            ? t("mintSpace.appMadePublic") || "App is now public"
            : t("mintSpace.appMadePrivate") || "App is now private"
        );
      }
    } catch (error) {
      setToast(
        error.message || t("mintSpace.appPropertiesFailed") || "Could not update visibility"
      );
    }
  };

  const handleAddAppAsProduct = (item) => {
    if (!isOwner || !item?.catalogAppId || item.catalogAppId === CREATOR_REWARDS_APP_ID) return;
    if (!item.instanceId) return;
    setOpenAppMenu(null);
    const appId = normalizeInstalledAppId(item.catalogAppId);
    if (appId === CONSULTING_APP_ID) {
      const navId = instanceNavId(item) || "consulting";
      setActiveNav(navId);
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set("nav", navId);
          next.set("offering", "new");
          next.delete("product");
          return next;
        },
        { replace: false }
      );
      return;
    }
    setPricingTarget({
      appId,
      instanceId: item.instanceId,
    });
    applyMintNav("pricing", { replace: false });
  };

  const openProductEditor = (productId) => {
    if (!spaceId || !productId) return;
    setActiveNav("pricing");
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set("nav", "pricing");
        next.set("product", String(productId));
        return next;
      },
      { replace: false }
    );
  };

  const openAffiliateForProduct = (productId) => {
    if (!productId) return;
    setAffiliateProductFocus(String(productId));
    applyMintNav("affiliate", { replace: false });
  };

  const canConfigureApp = (item) =>
    !!(
      item?.isCatalogApp &&
      isOwner &&
      item.catalogAppId &&
      item.catalogAppId !== CREATOR_REWARDS_APP_ID
    );

  const handleUninstallInstance = async (instanceId) => {
    if (!spaceId || !instanceId) return;
    try {
      const result = await uninstallApp(spaceId, null, instanceId);
      if (result.success) {
        setToast(t("mintSpace.appRemoved"));
        handleMintSpaceUpdate(result.data);
        applyMintNav("app-store", { replace: true });
      }
    } catch (error) {
      setToast(error.message || t("mintSpace.appRemoveFailed"));
    }
  };

  const renderNavButton = (item, layoutGroup = "mint-space-sidebar") => {
    const isActive = activeNav === item.id;
    const showAppMenu = canConfigureApp(item);

    const navButton = (
      <MintSpaceNavButton
        isActive={isActive}
        isDark={isDark}
        layoutGroup={layoutGroup}
        className={showAppMenu ? "pr-7" : ""}
        onClick={() => {
          if (item.id === "pricing") setPricingTarget(null);
          applyMintNav(item.id, { replace: false });
        }}
        onDoubleClick={
          showAppMenu
            ? (e) => {
                e.preventDefault();
                e.stopPropagation();
                openAppProperties(item);
              }
            : undefined
        }
      >
        <MintSpaceNavItemIcon item={item} isActive={isActive} />
        <SidebarTruncateTooltip text={item.label} isDark={isDark} />
        {item.visibility === "private" && (
          <FiLock
            className={`h-3 w-3 shrink-0 text-label-tertiary`}
            aria-hidden
          />
        )}
        {item.badge > 0 && (
          <span className="flex h-4 min-w-[1.1rem] shrink-0 items-center justify-center rounded-md bg-rose-500 px-1 text-caption-1-semibold text-white">
            {item.badge > 99 ? "99+" : item.badge}
          </span>
        )}
      </MintSpaceNavButton>
    );

    if (!showAppMenu && !(item.canDelete && isOwner)) {
      return (
        <div key={item.id}>
          {navButton}
        </div>
      );
    }

    return (
      <div key={item.id} className="group/app-nav relative">
        {navButton}
        {item.canDelete && isOwner && (
          <button
            type="button"
            aria-label={t("mintSpace.removeApp") || "Remove app"}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleUninstallInstance(item.instanceId);
            }}
            className={`absolute right-0.5 top-1/2 z-[2] inline-flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-md opacity-0 transition group-hover/app-nav:opacity-100 focus:opacity-100 ${
              "text-status-danger hover:bg-status-danger-bg"
            }`}
          >
            <FiTrash2 className="h-3.5 w-3.5" />
          </button>
        )}
        {showAppMenu && (
          <div
            className={`absolute top-1/2 z-[3] -translate-y-1/2 ${
              item.canDelete && isOwner ? "right-7" : "right-0.5"
            }`}
          >
            <button
              type="button"
              aria-label={t("mintSpace.appMenu") || "App menu"}
              aria-haspopup="menu"
              aria-expanded={openAppMenu?.item?.id === item.id}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (openAppMenu?.item?.id === item.id) {
                  setOpenAppMenu(null);
                  return;
                }
                appMenuAnchor.current = e.currentTarget;
                setOpenAppMenu({ item });
              }}
              className={`inline-flex h-6 w-6 items-center justify-center rounded-md opacity-0 transition group-hover/app-nav:opacity-100 focus:opacity-100 ${
                openAppMenu?.item?.id === item.id ? "opacity-100" : ""
              } ${
                isDark
                  ? "text-slate-300 hover:bg-white/10 hover:text-white"
                  : "text-slate-500 hover:bg-slate-200/80 hover:text-slate-900"
              }`}
            >
              <FiMoreHorizontal className="h-3.5 w-3.5" />
            </button>
          </div>
        )}
      </div>
    );
  };

  return (
    <AppScope app={PANEL_APPS[appNavIdToPageNav(activeNav, mintSpace)]} className="mint-space-window h-full w-full font-ds">
      <div
        className="h-full overflow-hidden"
      >
        <div className="flex h-full flex-col lg:flex-row">
          {/* Left Sidebar — only after access (owner / moderator / member) */}
          {(isOwner || isModerator || isMember) && (
          <div className="hidden w-[248px] flex-shrink-0 flex-col overflow-y-auto bg-bg-sidebar px-space-12 py-space-16 lg:flex">
            {/* On Home the page's own card shows all this, so the sidebar's slides up to one row. */}
            <div className="flex-shrink-0">
              <div
                aria-hidden={onHome}
                inert={onHome}
                className={`grid transition-[grid-template-rows,opacity] duration-300 ease-out ${
                  onHome ? "grid-rows-[0fr] opacity-0" : "grid-rows-[1fr] opacity-100"
                }`}
              >
                <div className="min-h-0 overflow-hidden">
                <div className="flex flex-shrink-0 flex-col gap-space-12">
                  {/* Figma "Cover": the banner, a 22px card */}
                  <div className="relative h-[96px] w-full rounded-[22px]">
                    {headerImagePreview ? (
                      <img
                        src={headerImagePreview}
                        alt={mintSpace?.name || "Mint Space"}
                        className="h-full w-full rounded-[22px] object-cover"
                      />
                    ) : (
                      <div
                        className="flex h-full w-full flex-col items-center justify-center rounded-[22px] bg-fill-quaternary"
                      >
                        {isOwner ? (
                          <label className="flex h-full w-full cursor-pointer flex-col items-center justify-center transition hover:bg-white/10">
                            <input
                              type="file"
                              ref={fileInputRef}
                              accept="image/*"
                              onChange={handleImageChange}
                              className="hidden"
                            />
                            <FiImage className={`mb-1 h-5 w-5 ${mutedText}`} />
                            <span className={`text-caption-1 font-medium ${mutedText}`}>
                              {t("mintSpace.addImage") || "Add Image"}
                            </span>
                          </label>
                        ) : (
                          <>
                            <FiImage className={`mb-2 h-8 w-8 ${mutedText}`} />
                            <span className={`text-subheadline font-medium ${mutedText}`}>No Image</span>
                          </>
                        )}
                      </div>
                    )}

                    {isBannerUploading && (
                      <div
                        className="absolute inset-0 z-[60] flex flex-col items-center justify-center bg-black/50 backdrop-blur-[2px]"
                        aria-live="polite"
                      >
                        <FiLoader className="h-8 w-8 animate-spin text-accent-text" />
                        <span className="mt-2 text-footnote-semibold text-white/95">
                          {t("profile.uploading")}
                        </span>
                      </div>
                    )}

                    <div className="absolute top-2 left-2 z-10 flex gap-1.5">
                      <button
                        type="button"
                        onClick={() => {
                          if (isBannerUploading) return;
                          handleMintSpaceBack();
                        }}
                        disabled={isBannerUploading}
                        className={`rounded-radius-xs p-1.5 transition disabled:pointer-events-none disabled:opacity-40 ${iconBtn}`}
                        aria-label="Back"
                      >
                        <FiArrowLeft className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (isBannerUploading) return;
                          shareOrCopyUrl(getShareUrl(`/mint-space/${spaceId}`), setToast, t);
                        }}
                        disabled={isBannerUploading}
                        className={`rounded-radius-xs p-1.5 transition disabled:pointer-events-none disabled:opacity-40 ${iconBtn}`}
                        title={t("common.share")}
                        aria-label={t("common.share")}
                      >
                        <FiShare2 className="h-4 w-4" />
                      </button>
                    </div>

                    {(isOwner || isModerator) && (
                      <div className="absolute top-2 right-2 z-10" ref={dropdownRef}>
                        <button
                          type="button"
                          onClick={() => {
                            if (isBannerUploading) return;
                            setEditDropdownOpen(!editDropdownOpen);
                          }}
                          disabled={isBannerUploading}
                          className={`rounded-radius-xs p-1.5 transition disabled:pointer-events-none disabled:opacity-40 ${iconBtn}`}
                          aria-label="Edit space"
                        >
                          <FiChevronDown
                            className={`h-4 w-4 transition-transform ${editDropdownOpen ? "rotate-180" : ""}`}
                          />
                        </button>
                        {/* Outside the menu, which closes before the file dialog answers. */}
                        <input ref={menuImageRef} type="file" accept="image/*" onChange={handleImageChange} className="hidden" />
                        <Menu
                          open={editDropdownOpen}
                          onClose={closeEditDropdown}
                          anchorRef={dropdownRef}
                          label={t("mintSpace.editSpace")}
                        >
                          <MenuItem icon="edit" onClick={() => setShowEditModal(true)}>
                            {t("mintSpace.editSpace")}
                          </MenuItem>
                          {headerImagePreview ? (
                            <MenuItem icon="close" danger onClick={handleRemoveImage}>
                              {t("mintSpace.removeImage")}
                            </MenuItem>
                          ) : (
                            <MenuItem icon="image" onClick={() => menuImageRef.current?.click()}>
                              {t("mintSpace.addImage")}
                            </MenuItem>
                          )}
                          <MenuSeparator />
                          <MenuItem
                            icon="trash"
                            danger
                            onClick={() => {
                              // Deleting lives in the dashboard's danger zone, which checks
                              // what's left to settle first.
                              setActiveNav("settings");
                              setSearchParams((prev) => {
                                const next = new URLSearchParams(prev);
                                next.set("nav", "settings");
                                next.set("section", "danger");
                                next.delete("campaign");
                                return next;
                              });
                            }}
                          >
                            {t("mintSpace.deleteMintSpace")}
                          </MenuItem>
                        </Menu>
                      </div>
                    )}

                    {!isOwner && mintSpace?.owner && (
                      <div className="absolute top-2 right-2 z-10">
                        <button
                          type="button"
                          onClick={() => {
                            if (isBannerUploading) return;
                            navigate(`/profile/${mintSpace.owner._id}`);
                          }}
                          disabled={isBannerUploading}
                          className={`rounded-radius-xs p-1.5 transition disabled:pointer-events-none disabled:opacity-40 ${iconBtn}`}
                          aria-label="View owner profile"
                          title={`View ${mintSpace.owner.username || mintSpace.owner.businessName}'s profile`}
                        >
                          <FiUser className="h-4 w-4" />
                        </button>
                      </div>
                    )}
                  </div>

                  <div className="flex flex-col gap-[2px]">
                    <h3 className="truncate text-headline text-label-primary">{mintSpace?.name}</h3>
                    {mintSpace?._id && (
                      <div className="mt-0.5">
                        <MintSpaceRatingStars
                          mintSpaceId={mintSpace._id}
                          isDark={isDark}
                          isOwner={isOwner}
                          isMember={isMember}
                          compact
                        />
                      </div>
                    )}
                    {isOwner && (
                      <BusinessVerificationBadge
                        status={verification.status}
                        onClick={() => verification.setOpen(true)}
                        t={t}
                        className="mt-1.5"
                      />
                    )}
                    {!isOwner && mintSpace?.owner && (
                      <div className="mt-1">
                        <UserProfileLink
                          user={mintSpace.owner}
                          avatarSize="xs"
                          isDark={isDark}
                          nameClassName={`text-caption-1 font-medium ${mutedText}`}
                          fallback={mintSpace.owner.username || mintSpace.owner.businessName || "—"}
                        />
                      </div>
                    )}
                  </div>
                </div>
                </div>
              </div>
              <div
                aria-hidden={!onHome}
                inert={!onHome}
                className={`grid transition-[grid-template-rows,opacity] duration-300 ease-out ${
                  onHome ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"
                }`}
              >
                <div className="min-h-0 overflow-hidden">
                  <div className="flex items-center gap-space-8">
                    <IconButton variant="plain" label={t("common.back")} onClick={handleMintSpaceBack}>
                      <DsIcon name="arrow-left" size={18} />
                    </IconButton>
                    <Avatar size="m" src={mintSpace?.logo} name={mintSpace?.name} />
                    <p className="min-w-0 flex-1 truncate text-headline text-label-primary">{mintSpace?.name}</p>
                  </div>
                </div>
              </div>
            </div>

            <LayoutGroup id="mint-space-sidebar-nav">
              <nav className="mt-space-12 flex-1 text-left">
                {adminNavItems.length > 0 && (
                  <>
                    <p className="mb-space-4 text-caps-group uppercase text-label-secondary">{t("windows.adminSettings")}</p>
                    <ul className="mb-space-12 flex flex-col gap-[2px]">
                      {adminNavItems.map((item) => (
                        <li key={item.id}>{renderNavButton(item)}</li>
                      ))}
                    </ul>
                  </>
                )}
                <p className="mb-space-4 text-caps-group uppercase text-label-secondary">Apps</p>
                <ul className="flex flex-col gap-[2px]">
                  {regularNavItems.map((item) => (
                    <li key={item.id}>{renderNavButton(item)}</li>
                  ))}
                </ul>
              </nav>
            </LayoutGroup>
          </div>
          )}

          {/* Phones and tablets: the bar in place of the sidebar */}
          {(isOwner || isModerator || isMember) && (
            <LayoutGroup id="mint-space-bar-nav">
              <SpaceBar
                space={mintSpace}
                current={spaceBarItems.find((item) => item.id === activeNav)?.label || mintSpace?.name}
                alert={spaceBarItems.some((item) => item.badge > 0)}
                isDark={isDark}
                onBack={handleMintSpaceBack}
                groups={[
                  ...(adminNavItems.length > 0
                    ? [
                        {
                          label: t("windows.adminSettings"),
                          items: (
                            <>
                              {isOwner && verification.status && (
                                <div className="mb-space-4 px-space-12">
                                  <BusinessVerificationBadge
                                    status={verification.status}
                                    onClick={() => verification.setOpen(true)}
                                    t={t}
                                  />
                                </div>
                              )}
                              {adminNavItems.map((item) => renderNavButton(item, "mint-space-bar"))}
                            </>
                          ),
                        },
                      ]
                    : []),
                  { label: "Apps", items: regularNavItems.map((item) => renderNavButton(item, "mint-space-bar")) },
                ]}
              />
            </LayoutGroup>
          )}

          {/* Main Content */}
          {/* The Dashboard's menu runs to the top of the window, so it clears the top bar itself. */}
          <div
            className={`flex min-h-0 flex-1 flex-col overflow-hidden ${
              appNavIdToPageNav(activeNav, mintSpace) === "settings" ? "" : "lg:pt-[88px]"
            }`}
          >
            {/* Creator Rewards' tabs, everywhere in the app but a single campaign */}
            {(isOwner || isModerator || isMember) &&
            !campaignIdFromPath &&
            CREATOR_TAB_PAGES.has(appNavIdToPageNav(activeNav, mintSpace)) && (
            <div className="flex-shrink-0 px-space-16 pb-space-8 min-[600px]:px-space-20 lg:px-space-24 lg:pt-space-16">
              <CreatorTabs
                label={mintSpace?.name || "Mint Space"}
                value={activeNav}
                onChange={(id) => applyMintNav(id, { replace: false })}
                options={navbarItems.map((item) => ({ value: item.id, label: item.label }))}
                selectedClass={SELECTED_CREATOR_TAB}
              />
            </div>
            )}

            {/* Content Area */}
            <div className="flex flex-1 flex-col min-h-0 overflow-hidden">
            {/* Someone not in the space: the join card, over this page */}
            {!isOwner && !isModerator && !isMember && (
              <SpaceGate
                space={mintSpace}
                isDark={isDark}
                setToast={setToast}
                onClose={handleMintSpaceBack}
                onJoined={(joinedSpace) => {
                  handleMintSpaceUpdate(joinedSpace);
                  // Wherever they came from, a campaign link included, a new member starts at Home.
                  applyMintNav("home", { replace: true, setCampaign: null });
                }}
              />
            )}
            {/* Show content if owner or member */}
            {(isOwner || isMember) && (
              <WindowNavPanels
                activeKey={activeNav}
                keepAlive={(navId) =>
                  !navId.startsWith("product-") &&
                  LIVE_SESSION_PANELS.has(appNavIdToPageNav(navId, mintSpace))
                }
                renderPanel={(navId) =>
                  navId.startsWith("product-")
                    ? renderMintNavPanel(navId)
                    : renderMintNavPanel(appNavIdToPageNav(navId, mintSpace), navId)
                }
              />
            )}
            </div>
          </div>
        </div>
      </div>

      <Modal
        open={showEditModal}
        onClose={() => setShowEditModal(false)}
        title={t("windows.editMintSpace")}
        closeLabel={t("common.close")}
        isDark={isDark}
      >
        <EditSpaceForm
          isDark={isDark}
          mintSpace={mintSpace}
          setToast={setToast}
          onSuccess={(updatedSpace) => {
            handleMintSpaceUpdate(updatedSpace);
            setShowEditModal(false);
          }}
        />
      </Modal>

      <Menu
        open={!!openAppMenu}
        onClose={closeAppMenu}
        anchorRef={appMenuAnchor}
        label={t("mintSpace.appMenu")}
      >
        <MenuItem icon={openAppMenu?.item?.visibility === "private" ? "eye" : "lock"} onClick={() => handleToggleAppVisibility(openAppMenu.item)}>
          {openAppMenu?.item?.visibility === "private" ? t("mintSpace.makeAppPublic") : t("mintSpace.makeAppPrivate")}
        </MenuItem>
        <MenuItem icon="settings" onClick={() => openAppProperties(openAppMenu.item)}>
          {t("mintSpace.appProperties")}
        </MenuItem>
        <MenuItem icon="bag" onClick={() => handleAddAppAsProduct(openAppMenu.item)}>
          {t("mintSpace.addAsProduct")}
        </MenuItem>
      </Menu>

      <MintSpaceAppPropertiesModal
        isOpen={!!appPropertiesTarget}
        onClose={() => setAppPropertiesTarget(null)}
        isDark={isDark}
        mintSpace={mintSpace}
        catalogApp={
          appCatalog.find((a) => a.id === appPropertiesTarget?.catalogAppId) || null
        }
        instance={
          appPropertiesTarget?.instanceId
            ? getInstanceById(mintSpace, appPropertiesTarget.instanceId)
            : null
        }
        setToast={setToast}
        onSaved={handleMintSpaceUpdate}
      />

      {/* Questions Modal */}
      <QuestionsModal
        isOpen={showQuestionsModal}
        setIsOpen={setShowQuestionsModal}
        questions={mintSpace?.questions || []}
        onSave={async (questions) => {
          try {
            const result = await updateMintSpace(spaceId, { questions });
            if (result.success) {
              setToast(t("toasts.questionsUpdated"));
              handleMintSpaceUpdate(result.data);
              setShowQuestionsModal(false);
            }
          } catch (error) {
            setToast(error.message || t("mintSpace.errorUpdatingQuestions") || "Error updating questions");
          }
        }}
        isDark={isDark}
        setToast={setToast}
      />

      <BusinessVerificationModal
        open={isOwner && verification.open}
        onClose={() => verification.setOpen(false)}
        isDark={isDark}
        t={t}
        mintSpaceId={mintSpace?._id}
        status={verification.status}
        onStatusChange={verification.setStatus}
        setToast={setToast}
      />
    </AppScope>
  );
};

export default YourMintSpaceWindow;

